import React, { useState } from 'react';
import { 
  TrendingUp, 
  Users, 
  Target, 
  BarChart3,
  Megaphone,
  Heart,
  Eye,
  MousePointer,
  Share2,
  Award,
  DollarSign,
  Activity
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, FunnelChart } from 'recharts';

const MarketingDashboard: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('monthly');
  const [selectedChannel, setSelectedChannel] = useState('all');

  // Marketing performance data
  const acquisitionData = [
    { month: 'Jan', organic: 1200, paid: 800, social: 600, email: 400, total: 3000, cost: 45000 },
    { month: 'Feb', organic: 1350, paid: 920, social: 680, email: 450, total: 3400, cost: 48000 },
    { month: 'Mar', organic: 1180, paid: 1100, social: 720, email: 380, total: 3380, cost: 52000 },
    { month: 'Apr', organic: 1420, paid: 1250, social: 800, email: 520, total: 3990, cost: 55000 },
    { month: 'May', organic: 1580, paid: 1180, social: 850, email: 590, total: 4200, cost: 51000 },
    { month: 'Jun', organic: 1650, paid: 1320, social: 920, email: 610, total: 4500, cost: 58000 },
  ];

  const channelPerformanceData = [
    { channel: 'Organic Search', customers: 1650, cost: 8000, cac: 4.85, ltv: 450, roi: 9200 },
    { channel: 'Paid Search', customers: 1320, cost: 35000, cac: 26.52, ltv: 380, roi: 1434 },
    { channel: 'Social Media', customers: 920, cost: 12000, cac: 13.04, ltv: 320, roi: 2453 },
    { channel: 'Email Marketing', customers: 610, cost: 3000, cac: 4.92, ltv: 520, roi: 10567 },
    { channel: 'Content Marketing', customers: 480, cost: 6000, cac: 12.50, ltv: 410, roi: 3280 },
  ];

  const brandHealthData = [
    { metric: 'Brand Awareness', current: 68, target: 75, change: 5.2 },
    { metric: 'Brand Consideration', current: 45, target: 50, change: 3.8 },
    { metric: 'Brand Preference', current: 32, target: 40, change: 2.1 },
    { metric: 'Net Promoter Score', current: 72, target: 70, change: 8.5 },
    { metric: 'Customer Satisfaction', current: 4.6, target: 4.5, change: 0.3 },
  ];

  const campaignData = [
    { name: 'AI Personalisation Launch', impressions: 2400000, clicks: 48000, conversions: 1200, spend: 25000, roi: 340 },
    { name: 'Mobile App Promotion', impressions: 1800000, clicks: 36000, conversions: 900, spend: 18000, roi: 280 },
    { name: 'Summer Sale Campaign', impressions: 3200000, clicks: 64000, conversions: 1600, spend: 32000, roi: 450 },
    { name: 'Brand Awareness Drive', impressions: 5000000, clicks: 75000, conversions: 750, spend: 40000, roi: 125 },
  ];

  // Key marketing metrics
  const marketingMetrics = [
    {
      id: 'customer_acquisition_cost',
      name: 'Customer Acquisition Cost',
      value: 12.89,
      change: -15.2,
      positive: true,
      description: 'Average cost to acquire a new customer',
      target: 15.00,
      unit: '£',
      volume: '4,500 customers'
    },
    {
      id: 'customer_lifetime_value',
      name: 'Customer Lifetime Value',
      value: 420,
      change: 8.7,
      positive: true,
      description: 'Average revenue per customer over lifetime',
      target: 380,
      unit: '£',
      volume: 'Avg 18 months'
    },
    {
      id: 'marketing_roi',
      name: 'Marketing ROI',
      value: 3.2,
      change: 12.3,
      positive: true,
      description: 'Return on marketing investment',
      target: 3.0,
      unit: 'x',
      volume: '£58K spend'
    },
    {
      id: 'conversion_rate',
      name: 'Conversion Rate',
      value: 2.8,
      change: 0.4,
      positive: true,
      description: 'Percentage of visitors who convert',
      target: 2.5,
      unit: '%',
      volume: '160K visitors'
    }
  ];

  const getChannelColor = (channel: string) => {
    const colors: { [key: string]: string } = {
      'Organic Search': '#10B981',
      'Paid Search': '#3B82F6',
      'Social Media': '#8B5CF6',
      'Email Marketing': '#F59E0B',
      'Content Marketing': '#EF4444'
    };
    return colors[channel] || '#6B7280';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <TrendingUp className="w-8 h-8 text-orange-400" />
            <span>Marketing Performance Dashboard</span>
          </h1>
          <p className="text-gray-400">Customer acquisition, brand health, and marketing ROI analytics</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="weekly">Weekly View</option>
            <option value="monthly">Monthly View</option>
            <option value="quarterly">Quarterly View</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Marketing Data</span>
          </div>
        </div>
      </div>

      {/* Key Marketing Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {marketingMetrics.map((metric) => (
          <div key={metric.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
              {metric.positive ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingUp className="w-5 h-5 text-red-500 transform rotate-180" />
              )}
            </div>
            <div className="flex items-baseline space-x-2 mb-2">
              <span className="text-2xl font-bold text-white">{metric.unit === '£' ? '£' : ''}{metric.value}{metric.unit !== '£' ? metric.unit : ''}</span>
            </div>
            <div className="flex items-center justify-between mb-3">
              <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                {metric.positive ? '+' : ''}{metric.change}%
              </span>
              <span className="text-xs text-gray-400">{metric.volume}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                style={{ width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>

      {/* Customer Acquisition & Channel Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Acquisition Trend */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-400" />
            <span>Customer Acquisition by Channel</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={acquisitionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Area type="monotone" dataKey="organic" stackId="1" stroke="#10B981" fill="#10B981" />
              <Area type="monotone" dataKey="paid" stackId="1" stroke="#3B82F6" fill="#3B82F6" />
              <Area type="monotone" dataKey="social" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" />
              <Area type="monotone" dataKey="email" stackId="1" stroke="#F59E0B" fill="#F59E0B" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Channel ROI Analysis */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Target className="w-5 h-5 text-green-400" />
            <span>Channel ROI Performance</span>
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={channelPerformanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="channel" stroke="#9CA3AF" angle={-45} textAnchor="end" height={80} />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar dataKey="roi" fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Brand Health & Campaign Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Brand Health Metrics */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Heart className="w-5 h-5 text-pink-400" />
            <span>Brand Health Metrics</span>
          </h3>
          <div className="space-y-4">
            {brandHealthData.map((metric, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-white font-medium">{metric.metric}</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-400">+{metric.change}%</span>
                    <span className="text-sm text-gray-400">Target: {metric.target}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex-1 bg-gray-600 rounded-full h-3">
                    <div 
                      className="bg-pink-500 h-3 rounded-full transition-all duration-300" 
                      style={{ width: `${(metric.current / 100) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-white">{metric.current}{metric.metric === 'Customer Satisfaction' ? '/5' : '%'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Campaign Performance */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Megaphone className="w-5 h-5 text-purple-400" />
            <span>Campaign Performance</span>
          </h3>
          <div className="space-y-4">
            {campaignData.map((campaign, index) => (
              <div key={index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">{campaign.name}</h4>
                  <span className={`text-sm font-bold ${campaign.roi >= 300 ? 'text-green-400' : campaign.roi >= 200 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {campaign.roi}% ROI
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-gray-400">Impressions</div>
                    <div className="text-white font-medium">{(campaign.impressions / 1000000).toFixed(1)}M</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Clicks</div>
                    <div className="text-white font-medium">{(campaign.clicks / 1000).toFixed(0)}K</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Conversions</div>
                    <div className="text-white font-medium">{campaign.conversions}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">Spend</div>
                    <div className="text-white font-medium">£{(campaign.spend / 1000).toFixed(0)}K</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marketing Funnel & Attribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Marketing Funnel */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5 text-indigo-400" />
            <span>Marketing Funnel</span>
          </h3>
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Awareness</span>
                <span className="text-white font-bold">2.4M</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-3">
                <div className="bg-blue-500 h-3 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Interest</span>
                <span className="text-white font-bold">480K</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-3">
                <div className="bg-green-500 h-3 rounded-full" style={{ width: '80%' }}></div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Consideration</span>
                <span className="text-white font-bold">96K</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-3">
                <div className="bg-yellow-500 h-3 rounded-full" style={{ width: '60%' }}></div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">Purchase</span>
                <span className="text-white font-bold">4.5K</span>
              </div>
              <div className="w-full bg-gray-600 rounded-full h-3">
                <div className="bg-purple-500 h-3 rounded-full" style={{ width: '40%' }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Attribution Analysis */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Share2 className="w-5 h-5 text-cyan-400" />
            <span>Attribution Analysis</span>
          </h3>
          <div className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">First Touch Attribution</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Organic Search</span>
                  <span className="text-white">42%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Paid Search</span>
                  <span className="text-white">28%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Social Media</span>
                  <span className="text-white">18%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Direct</span>
                  <span className="text-white">12%</span>
                </div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3">Last Touch Attribution</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Direct</span>
                  <span className="text-white">35%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Email</span>
                  <span className="text-white">25%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Paid Search</span>
                  <span className="text-white">22%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Organic Search</span>
                  <span className="text-white">18%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Media & Content Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Eye className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Social Reach</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">1.2M</div>
          <div className="text-sm text-green-400">+18.5% vs last month</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <MousePointer className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Engagement Rate</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">4.8%</div>
          <div className="text-sm text-green-400">+0.7% vs last month</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Share2 className="w-6 h-6 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">Share Rate</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">2.1%</div>
          <div className="text-sm text-green-400">+0.3% vs last month</div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex items-center space-x-3 mb-4">
            <Award className="w-6 h-6 text-yellow-400" />
            <h3 className="text-lg font-semibold text-white">Brand Mentions</h3>
          </div>
          <div className="text-3xl font-bold text-white mb-2">8.4K</div>
          <div className="text-sm text-green-400">+22.1% vs last month</div>
        </div>
      </div>
    </div>
  );
};

export default MarketingDashboard;